/**
 * 一笔交易的**逐笔明细**（多笔构成）—— 抽屉与行内折叠**共用**（`DR-066` D3 · `AC-B5.6`）。
 *
 * ## 🔴 为什么抽成组件（防双实现）
 *
 * 修前只有抽屉里有一段逐腿 `List`；负责人 2026-10-28 第 7 条要求"行上也能展开看构成"。
 * 若在流水行里再写一段，同一笔的构成就有了两份实现 —— 而它们的失败方式是**静默**
 * （两处金额算法不同、或一处显示了优惠另一处没有），只有用户对照才发现（铁律 24）。
 *
 * ## 两个变体，同一份数据
 *
 * | 变体 | 用在哪 | 显示 |
 * |---|---|---|
 * | `compact` | 流水行的折叠区 | 账户 · 分类 · 金额 · 优惠 |
 * | `full` | 详情抽屉 | 上面全部 **+ 归属人 + 币种 + 不计收支** |
 *
 * 🔴 `full` 不是"另一套实现"：它只是**多显示几个字段**，金额与优惠的取数完全同一段代码。
 *
 * ## 🔴 两个 `data-testid` 不许改名
 *
 * `leg-<legId>` 与逐腿优惠文案（`优惠 ¥x.xx`）是 `TransactionDrawer.spec.tsx` 与
 * `pages/*.spec.tsx` 的断言靶子。搬组件时顺手改名 = **改变式回归**：
 * 断言红了你只会以为是"文案变了"，实际是把回归守卫整片拆掉了。
 */

import { List, ListItem, ListItemText, Typography } from '@mui/material';
import { formatMoneyText, type Cents, type TransactionLegView } from '@jiaying/shared';

import { legCategoryText, legSignedCents } from '../features/journal-labels.js';
import { Amount } from './Amount.js';

export interface TxnLegsListProps {
  /** 全部腿（顺序即落库顺序 —— 本组件不重排、不过滤） */
  readonly legs: readonly TransactionLegView[];
  /**
   * `compact` = 列表折叠区（省掉归属人与币种，行高才不炸）；
   * `full` = 抽屉（明细要能自证：谁的钱 · 什么币种 · 是否计入收支）。
   */
  readonly variant: 'compact' | 'full';
  /** 折叠区的紧凑密度（抽屉里也用它，与旧实现一致） */
  readonly dense?: boolean;
}

/**
 * 一条腿的**主文案** = `账户 · 分类 [· 备注]`。
 *
 * 🔴 批 `117`：**每笔自己的备注必须显示**（负责人 2026-10-28 反馈「拆分流水显示的时候，
 * 需要把备注也带上」）。修前 `legs.note` 一路落库、一路下发到端侧，却**没有任何地方渲染**
 * —— 拆成 3 笔后三行的"是什么"只剩分类名，`食材 / 日用品 / 药` 这类子笔备注全部看不见。
 *
 * 🔴 备注**只取腿级**（`leg.note`），不拿交易级 `row.note` 兜底：多笔时交易级备注
 * 已经显示在行的副标题上（`journal-labels.ts` 的 `rowSubtitle`），在这里再兜一次
 * 会让同一句话在折叠区里重复 N 遍。
 * ⚠️ 空白串 ≡ 没有备注（与 `journal-labels.noteOf` 同一归一口径），否则 `'  '` 会渲染出一个空尾巴。
 */
function legPrimaryText(leg: TransactionLegView): string {
  // 🆕 批 153：无分类时按「不计收支 / 支出 / 收入」给文字（不再写「未分类」）
  const head = `${leg.accountName} · ${legCategoryText(leg)}`;
  const note = (leg.note ?? '').trim();
  return note === '' ? head : `${head} · ${note}`;
}

export function TxnLegsList({ legs, variant, dense = true }: TxnLegsListProps) {
  return (
    <List dense={dense} disablePadding>
      {legs.map((leg) => {
        /*
          🔴 逐腿金额用 `Amount`（`currency` 必填 ⇒ 外币腿不会被渲染成人民币）。
          着色按**方向**（红=流入 / 绿=流出，`AC-C1.6`）—— 腿金额恒正、方向在 `direction` 上
          ⇒ 必须经 `legSignedCents()` 投影出带符号的钱数，否则两条腿都会渲染成"正向红"。
        */
        const secondary =
          variant === 'full'
            ? // 🔴 归属人是**腿级**的（多腿可分属不同人）；不计收支的腿必须标注（`AC-C1.7`）
              `${leg.ownerName} · ${leg.currency}` +
              (leg.excludeFromStats ? ' · 不计收支' : '') +
              /*
                🆕 批 `110`：**优惠填了必须看得见**（修前 `discount` 下发了却无人渲染）。
                🔴 逐**腿**显示（优惠本就是腿级的）；金额列仍是实付净额，
                所以「实付 + 优惠」两行就够还原原价，不再多画一行「原价」。
              */
              (leg.discount > 0 ? ` · 优惠 ${formatMoneyText(leg.discount as Cents, leg.currency)}` : '')
            : // 紧凑变体：只留"这笔花在哪" —— 归属人与币种在抽屉里看
              (leg.discount > 0 ? `优惠 ${formatMoneyText(leg.discount as Cents, leg.currency)}` : '');

        return (
          <ListItem key={leg.id} disableGutters data-testid={`leg-${leg.id}`}>
            <ListItemText
              primary={legPrimaryText(leg)}
              secondary={secondary === '' ? undefined : secondary}
            />
            <Amount
              cents={legSignedCents(leg)}
              currency={leg.currency}
              signed
              variant="body2"
            />
          </ListItem>
        );
      })}
      {legs.length === 0 && (
        // 理论上不会发生（一笔交易至少一条腿）；显式说出来比渲染一片空白强
        <Typography variant="caption" color="text.secondary">
          这一笔没有明细
        </Typography>
      )}
    </List>
  );
}

export default TxnLegsList;
